import React, {memo, useState} from "react";
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import intl from "react-intl-universal";
import {useAppState, useRootStore, useUserState} from "@stores/AppStateContext";
import {useCrudServices} from "../../../platform/hooks/useServiceCombinations";

import type {ItemModelType} from "../../../stores/WorkspaceStore";

import {AddIcon} from "../../components/icons/AddIcon";
import {AddClusterIcon} from "../../components/icons/AddClusterIcon";
import {JumpToElementIcon} from "../../components/icons/JumpToElementIcon";
import {MoreVerticalIcon} from "../../components/icons/MoreVerticalIcon";
import {showInWorkspace} from "../../../actions/ui/showInWorkspace";
import {handleCreateCluster, handleCreateItem} from "../../../actions/utils/handleCreateItem";
import {Tooltip} from "../../components/Tooltip"

import {screenSizeProvider} from "../../../stores/ScreenSizeProvider";

export interface ItemButtonsProps {
    item: ItemModelType;
    parentIdFromTree?: string | null;
    index?: number;
    isInSearch?: boolean;
    inEditorView?: boolean;
}

export const ItemButtons = memo(observer((
    {
        item,
        parentIdFromTree,
        index,
        isInSearch = false,
        inEditorView = false,
    }: ItemButtonsProps) => {
    const appState = useAppState();
    const rootStore = useRootStore();
    const userState = useUserState();
    const crudServices = useCrudServices();
    const activeWorkspace = rootStore.view.workspace!;
    const dsManager = userState!.dsManager!;
    const [isMoreClicked, setMoreClicked] = useState(false);
    const isMobile = screenSizeProvider.view !== 'desktop';
    const _isReadOnly = userState!.isCurrentUserReadOnly;

    const onMoreClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (isMoreClicked) {
            appState.setContextMenu(null);
        } else {
            setMoreClicked(true);
            const targetDom = event.currentTarget;
            appState.addAdditionalContextMenu({
                    clientX: event.clientX,
                    clientY: event.clientY,
                    type: 'item',
                    id: item.id,
                    withOverlay: true,
                    menuPlacement: 'block-end',
                    menuAlignment: 'center',
                    withPointer: true,
                    getTargetBoundingBox: () => targetDom.getBoundingClientRect(),
                    params: {
                        itemId: item.id,
                        parentIdFromTree: parentIdFromTree,
                        index: index,
                        inEditorView: inEditorView,
                    },
                    onClose: () => {
                        setMoreClicked(false);
                    },
                }
            );
        }
    };

    const buttons = [];
    if (isInSearch) {
        buttons.push(
            <Tooltip key="SIT" title={intl.get('tooltip.showInTree').d("Show in Tree")} isInBox={false}
                     withPointer={true}>
                <JumpToElementIcon className='ItemLine__button'
                                   onClick={(event) => {
                                       event.stopPropagation();
                                       showInWorkspace(rootStore, item.id, activeWorkspace)
                                   }}
                                   size={16}
                                   fullHeight={true}
                                   additionalPadding={isMobile ? 4 : 2}
                />
            </Tooltip>
        )
    } else if (item.isCluster && !_isReadOnly) {
        buttons.push(
            <Tooltip key="CC" title={intl.get('tooltip.createPageInCluster').d("Create Page in Cluster")}
                     isInBox={false} withPointer={true}>
                <AddIcon className='ItemLine__button'
                         onClick={handleCreateItem(crudServices, activeWorkspace, appState, item)} size={16}
                         fullHeight={true}
                         additionalPadding={isMobile ? 4 : 2}
                />
            </Tooltip>
        );
        buttons.push(
            <Tooltip key="CP" title={intl.get('tooltip.createSubCluster').d("Create Sub-Cluster")}
                     isInBox={false} withPointer={true}>
                <AddClusterIcon className='ItemLine__button'
                                onClick={handleCreateCluster(crudServices, activeWorkspace, appState, item)} size={16}
                                fullHeight={true}
                                additionalPadding={isMobile ? 4 : 2}
                />
            </Tooltip>
        );
    }
    if (!_isReadOnly) {
        buttons.push(
            <Tooltip key="more" title={intl.get('tooltip.more').d("More")} withPointer={true} isInBox={false}>
                <MoreVerticalIcon
                    className='ItemLine__button ItemLine__button--more'
                    onClick={onMoreClick}
                    size={16}
                    fullHeight={true}
                    additionalPadding={isMobile ? 4 : 2}
                    additionalPaddingInlineEnd={isMobile ? 8 : undefined}
                />
            </Tooltip>
        )
    }

    return (
        <>
            {buttons.length >= 1
                ? <span className={classNames({
                    'ItemLine__buttons': true,
                    'ItemLine__buttons--bright': inEditorView,
                    'ItemLine__buttons--isMoreClicked': isMoreClicked,
                })
                }>
                    {buttons}
                  </span>
                : null
            }
        </>
    )
}));
